import React, {useEffect, useRef, useState} from "react";
import {makeStyles, useTheme} from '@material-ui/core/styles';
import {AppBar, Divider, Grid} from "@material-ui/core";
import {useDispatch, useSelector} from "../../store";
import {
    selectApplets,
    selectInboxCount,
    selectUserInfo,
    setInboxCount,
    setUserInfo
} from "../../reducer/iam/authReducer";
import { useHistory } from "react-router-dom";
import Button from "../../lib/appfuse-react/component/Button";
import Space from "../../lib/appfuse-react/component/Space";
import Typography from "@material-ui/core/Typography";
import SearchField from "../../component/SearchField";
import Expander from "../../lib/appfuse-react/component/Expander";
import Toolbar from "../../component/Toolbar";
import UserProfile from "./UserProfile";
import lang from "../../lib/appfuse-react/core/lang";
import {selectTitle} from "../../reducer/app/appReducer";
import i18n from "../../lib/appfuse-react/core/i18n";
import Dock, {DockItem} from "../../lib/appfuse-react/component/Dock";
import Box from "../../lib/appfuse-react/component/Box";
import env from "../../lib/appfuse-react/core/env";
import useRouter from "../../lib/appfuse-react/hook/useRouter";
import Image from "../../lib/appfuse-react/component/Image";
import authService from "../../service/iam/authService";
import TextField from "../../lib/appfuse-react/component/TextField";
import Form from "../../lib/appfuse-react/component/Form";
import usePrompt from "../../lib/appfuse-react/hook/usePrompt";
import {DialogActions, DialogContent, StyledDialog} from "../../component/Dialog";
import DataTable, {DataColumn} from "../../lib/appfuse-react/component/DataTable";
import myCustomersService from "../../service/myCustomers/myCustomersService";

const DEFAULTICON = 'image/application/webpage.svg';

const initialContext = {
    workspace: undefined
}

const DesktopContext = React.createContext(initialContext);


const useStyles = makeStyles((theme) => ({
    appBar: {
        zIndex: theme.zIndex.drawer + 100,
        backgroundColor: theme.brand.appbar?.backgroundColor,
        color: theme.brand.appbar?.color,
    }
}));

function Desktop(props) {
    const theme = useTheme();
    const router = useRouter();
    const prompt = usePrompt();
    const backgroundImage = router.resolve(theme.brand.backgroundImage);
    const classes = useStyles({backgroundImage});
    const history = useHistory();
    const dispatch = useDispatch();
    const [keyword, setKeyword] = useState(null);
    const [userProfileAnchorEl, setUserProfileAnchorEl] = useState(null);
    const title = useSelector(selectTitle);
    const userInfo = useSelector(selectUserInfo);
    const applets = useSelector(selectApplets);
    const inboxCount = useSelector(selectInboxCount);
    const responsive = env.responsive;
    const workspaceRef = useRef();
    const workspace = workspaceRef?.current;
    const { cropCustomer, processingCustomer, category, certificationArea } = userInfo;
    const { count, cropCount, processingCount } = inboxCount;

    const [switchCustomerOpen, setSwitchCustomerOpen] = useState(false);
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const location = props.children.props.location;
    const {search} = location;
    const urlParams = new URLSearchParams(search);
    const changePassword = urlParams.get("changePassword");
    if (lang.isNotEmpty(changePassword) && changePassword==="true" && lang.isFalse(showChangePassword)) {
        setChangePasswordOpen(true);
        setShowChangePassword(true);
    }

    useEffect(() => {
        getUserInfo().catch();
    },[]);

    const getUserInfo = async () => {
        try {
            const userInfo = await authService.fetchUserInfo();
            //帳號類別是客戶才需fetch代辦事項件數
            if (userInfo?.category === "customer") {
                await getInboxCount();
            }
            dispatch(setUserInfo(userInfo));
        } catch (e) {
            prompt.alert(e,{title: "Failed to fetch User info."});
        }
    }

    const getInboxCount = async () => {
        try {
            const inboxCounts = await authService.fetchInboxCounts();
            dispatch(setInboxCount(inboxCounts));
        } catch (e) {
            prompt.alert(e, {title: 'Failed to fetch counts of inbox.'});
        }
    }

    const handleDockItemClick = (event) => {
        const { value } = event;
        history.push(`/${value}`);
    };

    const handleToggleUserProfile = (event) => {
        setUserProfileAnchorEl(userProfileAnchorEl ? null : event.currentTarget);
    };

    const handleUserProfileClose = () => {
        setUserProfileAnchorEl(null);
    };

    const handleChangePasswordOpen = () => setChangePasswordOpen(true);
    const handleChangePasswordClose = () => setChangePasswordOpen(false);
    const handleChangePassword = async (value) => {
        await authService.changePassword(value);
    };

    const handleLogout = () => {
        setUserProfileAnchorEl(null);
        authService.logout();
        router.reload();
    }

    const handleSearch = event => {
        const keyword = event.value;
        setKeyword(keyword);
    }

    const handleDirectToInbox = () => {
        let url = "";
        if (cropCount > 0) url = `/cropinbox`;
        else if (processingCount > 0) url = `/processinginbox`;
        else if (userInfo.cropCustomer) url = `/cropinbox`;
        else if (userInfo.processingCustomer) url = `/processinginbox`;
        history.push(url);
    }

    const handleSwitchToCustomer = async (requestBody) => {
        try {
            await myCustomersService.switchUser(requestBody);
            await getUserInfo();
            handleSwitchToCustomerClose();
        } catch (e) {
            prompt.alert(e, {title: 'Failed to switch.'});
        }
    }
    const handleSwitchToCustomerOpen = () => {
        setSwitchCustomerOpen(true);
    }

    const handleSwitchToCustomerClose = () => {
        setSwitchCustomerOpen(false);
    }

    window.document.title = i18n.translate(title);

    const dockItems = applets
        .filter(applet => applet?.category === category)
        .filter(applet => {
            const { crop, processing } = applet;
            let showIcon = false;
            if ((cropCustomer && processingCustomer) || (crop && processing)) {
                showIcon = crop || processing;
            } else if (cropCustomer) {
                showIcon = crop;
            } else if (processingCustomer) {
                showIcon = processing;
            } else {
                showIcon = true;
            }
            return showIcon;
        })
        .filter(applet => {
            let showIcon = true;
            if (applet.id === "ProcessingAgriculturalProductOperator" && certificationArea !== "overseas") {
                showIcon = false;
            }
            return showIcon;
        })
        .map(applet => {
        const { id, path, text = 'Unknown', icon = DEFAULTICON, locked, disabled, badge, description } = applet;

        return (
            <DockItem key={id} icon={icon} text={text} description={description} locked={locked} disabled={disabled} badge={badge} value={path}
                      onClick={handleDockItemClick}/>
        )
    });

    const settings = env.get('app');
    const stageInfo = env.appStage.toUpperCase() === 'PROD' ? null : env.appStage;
    const versionInfo = env.appVersion + `${stageInfo ? '@' + stageInfo: ''}`;
    return (
        <Box className="app-desktop" fullWidth fullHeight overflow="hidden" display="flex" flexDirection="column">
            <AppBar position="static" className={classes.appBar}>
                <Toolbar>
                    <Image value={theme.brand.logo} onClick={()=> history.push('/main')} height={52} title={versionInfo} />
                    <Space spacing={1} />
                    <Typography variant="h5" noWrap>
                        {i18n.translate(title)}
                        <Space spacing={1} />
                        {stageInfo ? <Typography variant="body2" display="inline">{versionInfo}</Typography> : null}
                    </Typography>
                    <Box display={lang.isFalse(settings.search) ? 'none' : 'initial'}>
                        <SearchField label="Search" value={keyword} onChange={handleSearch} />
                    </Box>
                    <Expander />
                    <Typography variant="subtitle1" noWrap>{`${i18n.translate('Welcome')} ${userInfo?.name || ''}`}</Typography>
                    <Button icon="mail" variant="icon" badge={count}  color="inherit" onClick={handleDirectToInbox}/>
                    <Button icon="account_circle" variant="icon"  color="inherit" onClick={handleToggleUserProfile} />
                </Toolbar>
            </AppBar>
            <Box fullHeight fullWidth backgroundImage={backgroundImage} display="flex" overflow="hidden">
                <Box fullHeight fullWidth flexGrow={1} overflow="auto">
                    <Box ref={workspaceRef} fullHeight fullWidth overflow="hidden" minWidth={`${responsive.minWidth}px`}>
                        <DesktopContext.Provider value={{workspace}}>
                            {props.children}
                        </DesktopContext.Provider>
                    </Box>
                </Box>
                <Dock translucent>{dockItems}</Dock>
            </Box>
            <UserProfile anchorEl={userProfileAnchorEl} userInfo={userInfo}
                         open={lang.isNotNullOrUndefined(userProfileAnchorEl)}
                         onClose={handleUserProfileClose} onChangePassword={handleChangePasswordOpen} onLogout={handleLogout}
                         onSwitchToCustomer={handleSwitchToCustomerOpen}/>
            { changePasswordOpen && <ChangePassword open={changePasswordOpen} onClose={handleChangePasswordClose} onApply={handleChangePassword}/> }
            <SwitchToCustomer open={switchCustomerOpen} onClose={handleSwitchToCustomerClose} onSwitch={handleSwitchToCustomer}/>
        </Box>
    )
}

function ChangePassword(props){
    const [value, setValue] = useState({});
    const prompt = usePrompt();

    const handleChange = event => setValue(event.value);

    const handleOK = async () => {
        if(lang.isEmpty(value?.newPassword)) {
            prompt.confirm('Please enter the new password.');
            return;
        } else if(lang.isEmpty(value?.confirmNewPassword)) {
            prompt.confirm('Please enter the new password again.');
            return;
        }
        try {
            await props.onApply(value);
            prompt.success('Password changed successfully.');
            setValue({});
            props.onClose();
        } catch (e) {
            prompt.alert(e, {title: 'Failed to change password.'});
        }
    }

    return (
        <StyledDialog open={props.open} onClose={props.onClose} maxWidth='xs'>
            <Box paddingTop={2}>
                <Typography align='center' variant='h6'>{i18n.translate('Forget Password')}</Typography>
            </Box>
            <Divider variant="middle"/>
            <DialogContent>
                <Box fullHeight fullWidth overflow='auto' paddingTop={2} paddingBottom={5} paddingX={2}>
                    <Form value={value} onChange={handleChange} onSubmit={handleOK}>
                        <Grid container spacing={2}>
                            {/*輸入新密碼*/}
                            <Grid item xs={12}>
                                <TextField name='newPassword' label='Please enter new password' variant='outlined' fullWidth required type="password"/>
                            </Grid>
                            {/*再次輸入新密碼*/}
                            <Grid item xs={12}>
                                <TextField name='confirmNewPassword' label='Enter new password again' variant='outlined' fullWidth required type="password"/>
                            </Grid>
                            <Grid item xs={6}>
                                <Button icon='save' text='OK' variant='contained' color='primary' fullWidth onClick={handleOK}/>
                            </Grid>
                            <Grid item xs={6}>
                                <Button icon='close' text='Cancel' variant='contained' color='primary' fullWidth onClick={props.onClose}/>
                            </Grid>
                        </Grid>
                    </Form>
                </Box>
            </DialogContent>
        </StyledDialog>
    )
}

function SwitchToCustomer(props){
    const [data, setData] = useState([]);
    const [selection, setSelection] = useState([]);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        (async () => {
            await search();
            setReady(true);
        })()
    },[]);

    const search = async () => {
        const data = await myCustomersService.find();
        setData(data);
    }

    const handleSwitchUser = async () => {
        if (selection?.length === 1) {
            const row = lang.first(selection);
            const detail = data[row];
            const requestBody = {customerAccount: detail?.customerAccount};
            await props.onSwitch(requestBody);
        }
    }

    const handSelect = event => setSelection(event.selection);

    return (
        <StyledDialog open={props.open} onClose={props.onClose} >
            <Box paddingTop={2}>
                <Typography align='center' variant='h6'>{i18n.translate('Switch to customer')}</Typography>
            </Box>
            <Box marginY={1}>
                <Divider variant="fullWidth"/>
            </Box>
            <DialogContent>
                <Box fullWidth fullHeight ready={ready}>
                    <DataTable data={data} selection={selection} onSelect={handSelect}
                               stickyHeader maxHeight="100%" dense>
                        <DataColumn accessor="customerAccount" label="Customer account" align="left"/>
                        <DataColumn accessor="customerNo" label="Operator number" />
                        <DataColumn accessor="customerName" label="Customer name"/>
                        <DataColumn accessor="projectManager" label="Project manager"/>
                    </DataTable>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button icon='save' text='Switch' variant='contained' color='primary' fullWidth onClick={handleSwitchUser}/>
                <Button icon='close' text='Cancel' variant='contained' color='primary' fullWidth onClick={props.onClose}/>
            </DialogActions>
        </StyledDialog>
    )
}
const withDesktop = WrappedComponent => (props) => {
    return (
        <Desktop>
            <WrappedComponent {...props} />
        </Desktop>
    );
};

export default Desktop;

export { withDesktop, DesktopContext };